import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';

function StatusBadge({ status }) {
    const cls = `badge badge-${(status || '').toLowerCase()}`;
    return <span className={cls}>{status}</span>;
}

function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatMoney(v) {
    if (!v) return '';
    return '$' + Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function SearchPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Filter state from URL params
    const [filters, setFilters] = useState({
        q: searchParams.get('q') || '',
        status: searchParams.get('status') || '',
        shop: searchParams.get('shop') || '',
        department: searchParams.get('department') || '',
        category: searchParams.get('category') || '',
        dateField: searchParams.get('dateField') || 'RequestedDate',
        dateFrom: searchParams.get('dateFrom') || '',
        dateTo: searchParams.get('dateTo') || '',
    });
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [pageSize] = useState(25);

    // Data
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Lookups
    const [departments, setDepartments] = useState([]);
    const [statuses, setStatuses] = useState([]);

    // Load lookups on mount
    useEffect(() => {
        api.departments().then(setDepartments).catch(() => {});
        api.statuses().then(setStatuses).catch(() => {});
    }, []);

    // Search function
    const doSearch = useCallback(async (f, p) => {
        setLoading(true);
        setError(null);
        try {
            const params = { page: p, pageSize };
            if (f.q) params.q = f.q;
            if (f.status) params.status = f.status;
            if (f.shop) params.shop = f.shop;
            if (f.department) params.department = f.department;
            if (f.category) params.category = f.category;
            if (f.dateField) params.dateField = f.dateField;
            if (f.dateFrom) params.dateFrom = f.dateFrom;
            if (f.dateTo) params.dateTo = f.dateTo;

            const data = await api.searchWorkOrders(params);
            setResults(data);

            // Update URL params
            const sp = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => { if (v) sp.set(k, v); });
            setSearchParams(sp, { replace: true });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [pageSize, setSearchParams]);

    // Search on page change (but not on initial mount)
    const hasSearched = React.useRef(false);
    useEffect(() => {
        if (hasSearched.current) doSearch(filters, page);
    // eslint-disable-next-line
    }, [page]);

    const handleSearch = (e) => {
        e.preventDefault();
        hasSearched.current = true;
        setPage(1);
        doSearch(filters, 1);
    };

    const handleClear = () => {
        const cleared = { q: '', status: '', shop: '', department: '', category: '', dateField: 'RequestedDate', dateFrom: '', dateTo: '' };
        setFilters(cleared);
        setPage(1);
        doSearch(cleared, 1);
    };

    const updateFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div>
            <form onSubmit={handleSearch}>
                <div className="card">
                    <div className="filters">
                        <div className="filter-group">
                            <label>WO ID</label>
                            <input type="text" placeholder="e.g. 499328 or VIC-499328" value={filters.q}
                                onChange={e => updateFilter('q', e.target.value)} style={{ width: 190 }}
                                title="The number on its own is enough - the state prefix is optional" />
                        </div>
                        <div className="filter-group">
                            <label>Status</label>
                            <select value={filters.status} onChange={e => updateFilter('status', e.target.value)} style={{ width: 140 }}>
                                <option value="">All</option>
                                {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>Site #</label>
                            <input type="text" placeholder="e.g. 3103" value={filters.shop}
                                onChange={e => updateFilter('shop', e.target.value)} style={{ width: 100 }} />
                        </div>
                        <div className="filter-group">
                            <label>Department</label>
                            <select value={filters.department} onChange={e => updateFilter('department', e.target.value)} style={{ width: 160 }}>
                                <option value="">All</option>
                                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>Category ID</label>
                            <input type="text" placeholder="e.g. R&M, SIGN" value={filters.category}
                                onChange={e => updateFilter('category', e.target.value)} style={{ width: 120 }} />
                        </div>
                        <div className="filter-group">
                            <label>Date Field</label>
                            <select value={filters.dateField} onChange={e => updateFilter('dateField', e.target.value)} style={{ width: 130 }}>
                                <option value="RequestedDate">Requested</option>
                                <option value="IssuedDate">Issued</option>
                                <option value="CompleteDate">Completed</option>
                                <option value="ClosedDate">Closed</option>
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>From</label>
                            <input type="date" value={filters.dateFrom} onChange={e => updateFilter('dateFrom', e.target.value)} />
                        </div>
                        <div className="filter-group">
                            <label>To</label>
                            <input type="date" value={filters.dateTo} onChange={e => updateFilter('dateTo', e.target.value)} />
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                        <button type="button" className="btn btn-outline" onClick={handleClear}>Clear</button>
                    </div>
                </div>
            </form>

            {error && <div style={{ padding: 12, background: '#fce4ec', color: '#c62828', borderRadius: 4, marginBottom: 16 }}>{error}</div>}

            {results && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: '#666' }}>
                            {results.total.toLocaleString()} work orders found
                            {results.totalPages > 1 && ` -- page ${results.page} of ${results.totalPages}`}
                        </span>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>WO ID</th>
                                <th>Status</th>
                                <th>Site</th>
                                <th>Department</th>
                                <th>Category</th>
                                <th>Requester</th>
                                <th>Description</th>
                                <th>Requested</th>
                                <th style={{ textAlign: 'right' }}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.workorders.map(wo => (
                                <tr key={wo.pk} onClick={() => navigate(`/wo/${wo.pk}`)}>
                                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{wo.id}</td>
                                    <td><StatusBadge status={wo.status} /></td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{wo.shopId} {wo.shopName}</td>
                                    <td>{wo.departmentName}</td>
                                    <td>{wo.categoryName}</td>
                                    <td>{wo.requesterName}</td>
                                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {wo.reason}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(wo.requestedDate)}</td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMoney(wo.costTotalActual)}</td>
                                </tr>
                            ))}
                            {results.workorders.length === 0 && (
                                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#999' }}>No work orders found</td></tr>
                            )}
                        </tbody>
                    </table>

                    {results.totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                            <button className="btn btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                            <span style={{ padding: '8px 12px', fontSize: 13 }}>Page {page} of {results.totalPages}</span>
                            <button className="btn btn-outline" disabled={page >= results.totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
